<template>
  <div class="checkin-app">

    <!-- ── LOGIN ── -->
    <div v-if="!verifierToken" class="login-screen">
      <div class="login-card">
        <div class="login-logo">NFTWerks</div>
        <h2 class="login-title">Enter your verifier token</h2>
        <p class="login-sub">Use your token to access the scanner.</p>
        <input
          v-model="tokenInput"
          class="login-input"
          type="password"
          placeholder="Verifier token"
          @keyup.enter="doLogin"
        />
        <p v-if="loginError" class="login-error">{{ loginError }}</p>
        <button class="login-btn" :disabled="!tokenInput || loginLoading" @click="doLogin">
          {{ loginLoading ? 'Checking…' : 'Continue' }}
        </button>
        <p class="login-hint">Ask the event organiser for a verifier token.</p>
      </div>
    </div>

    <!-- ── MAIN APP (logged in) ── -->
    <template v-else>
      <!-- Header -->
      <div class="app-header">
        <div>
          <p class="app-event-label">{{ eventName || 'Event' }}</p>
          <span class="app-entry-badge" :class="{ 'app-entry-badge--out': scanMode === 'out' }">
            {{ scanMode === 'out' ? '↩ EXIT' : '✓ ENTRY' }}
          </span>
        </div>
        <button class="signout-btn" @click="signOut">Sign out</button>
      </div>

      <!-- Tabs -->
      <div class="app-tabs">
        <button class="app-tab" :class="{ active: tab === 'scan' }" @click="switchTab('scan')">Scan</button>
        <button class="app-tab" :class="{ active: tab === 'manual' }" @click="switchTab('manual')">Manual</button>
        <button class="app-tab" :class="{ active: tab === 'history' }" @click="switchTab('history'); loadHistory()">History</button>
      </div>

      <!-- ── SCAN TAB ── -->
      <div v-show="tab === 'scan'" class="tab-panel">
        <!-- Check In / Check Out mode toggle -->
        <div class="scan-mode-toggle" :class="{ 'scan-mode-toggle--hidden': scanResult }">
          <button
            class="scan-mode-btn"
            :class="{ active: scanMode === 'in' }"
            @click="setScanMode('in')"
          >Check In</button>
          <button
            class="scan-mode-btn scan-mode-btn--out"
            :class="{ active: scanMode === 'out' }"
            @click="setScanMode('out')"
          >Check Out</button>
        </div>

        <!-- Result overlay -->
        <div
          v-if="scanResult"
          class="result-overlay"
          :class="`result-overlay--${scanResult.result}`"
          @click="dismissResult"
        >
          <div class="result-icon">
            <span v-if="scanResult.result === 'valid'">✓</span>
            <span v-else-if="scanResult.result === 'reentry'">↩</span>
            <span v-else-if="scanResult.result === 'checked_out'">⇤</span>
            <span v-else-if="scanResult.result === 'used'">⏱</span>
            <span v-else-if="scanResult.result === 'reentry_blocked'">⇤</span>
            <span v-else-if="scanResult.result === 'not_checked_in'">⚠</span>
            <span v-else>✕</span>
          </div>
          <div class="result-word">
            <span v-if="scanResult.result === 'valid'">VALID</span>
            <span v-else-if="scanResult.result === 'reentry'">RE-ENTRY</span>
            <span v-else-if="scanResult.result === 'checked_out'">CHECKED OUT</span>
            <span v-else-if="scanResult.result === 'used'">USED</span>
            <span v-else-if="scanResult.result === 'reentry_blocked'">NO RE-ENTRY</span>
            <span v-else-if="scanResult.result === 'not_checked_in'">NOT CHECKED IN</span>
            <span v-else>INVALID</span>
          </div>
          <div v-if="scanResult.message" class="result-tier">{{ scanResult.message }}</div>
          <div v-if="scanResult.result === 'reentry' && scanResult.entryCount" class="result-entry-count">
            Entry {{ scanResult.entryCount }}
          </div>
          <div v-if="scanResult.result === 'reentry_blocked'" class="result-entry-count">Checked out earlier. This event doesn't allow re-entry.</div>
          <div v-if="scanResult.tierName" class="result-tier">{{ scanResult.tierName }} · {{ scanResult.qrCode }}</div>
          <div v-if="eventName" class="result-event">{{ eventName }}</div>
          <div class="result-hint">Tap anywhere to scan the next ticket</div>
        </div>

        <!-- Camera -->
        <div v-show="!scanResult" class="camera-wrap">
          <!-- Camera selector — only shown when device has multiple cameras -->
          <div v-if="cameras.length > 1" class="camera-selector">
            <button
              v-for="cam in cameras"
              :key="cam.id"
              class="camera-btn"
              :class="{ 'camera-btn--active': selectedCameraId === cam.id }"
              @click="switchCamera(cam.id)"
            >{{ cameraLabel(cam) }}</button>
          </div>
          <div id="qr-reader"></div>
          <p v-if="cameraError" class="camera-error">{{ cameraError }}</p>
        </div>
      </div>

      <!-- ── MANUAL TAB ── -->
      <div v-if="tab === 'manual'" class="tab-panel tab-panel--padded">
        <p class="panel-event-label">{{ eventName }}</p>
        <h2 class="panel-title">Check a ticket manually</h2>

        <div class="scan-mode-toggle">
          <button
            class="scan-mode-btn"
            :class="{ active: scanMode === 'in' }"
            @click="setScanMode('in')"
          >Check In</button>
          <button
            class="scan-mode-btn scan-mode-btn--out"
            :class="{ active: scanMode === 'out' }"
            @click="setScanMode('out')"
          >Check Out</button>
        </div>

        <label class="field-label">Ticket code <span class="required">*</span></label>
        <input
          v-model="manualCode"
          class="field-input"
          :placeholder="`eg. ${exampleCode}`"
          @keyup.enter="doManualVerify"
        />

        <div v-if="manualResult" class="manual-result">
          <div v-if="manualResult.result === 'invalid'" class="manual-result__invalid-text">{{ manualResult.message || 'Ticket not recognised' }}</div>
          <div v-else class="manual-result__info">
            <span class="manual-result__tier">{{ manualResult.tierName }}</span>
            <span class="manual-result__code">{{ manualResult.qrCode }}</span>
          </div>
          <span class="manual-badge" :class="`manual-badge--${manualResult.result}`">
            {{ manualResultLabel(manualResult.result) }}
          </span>
        </div>

        <button
          class="verify-btn"
          :disabled="!manualCode.trim() || manualLoading"
          @click="doManualVerify"
        >{{ manualLoading ? '…' : (scanMode === 'out' ? 'Check Out' : 'Verify') }}</button>

        <button
          v-if="manualResult && manualResult.qrCode"
          class="history-link-btn"
          @click="openTicketHistory(manualResult.qrCode)"
        >View ticket history →</button>
      </div>

      <!-- ── HISTORY TAB ── -->
      <div v-if="tab === 'history'" class="tab-panel tab-panel--padded">
        <p class="panel-event-label">{{ eventName }}</p>
        <h2 class="panel-title">Scan History</h2>

        <div class="history-filters">
          <button
            v-for="f in historyFilters"
            :key="f.key"
            class="filter-chip"
            :class="{ active: historyFilter === f.key }"
            @click="historyFilter = f.key"
          >{{ f.label }}</button>
        </div>

        <div v-if="historyLoading" class="state-msg">Loading…</div>
        <div v-else-if="!filteredHistory.length" class="state-msg">No scans yet.</div>

        <div v-else class="history-list">
          <div
            v-for="log in filteredHistory"
            :key="log.id"
            class="history-row"
            @click="openTicketHistory(log.qrCode)"
          >
            <span class="history-dot" :class="`history-dot--${log.action}`"></span>
            <div class="history-row__info">
              <span class="history-row__action">{{ actionLabel(log.action) }}</span>
              <span v-if="log.tierName" class="history-row__tier">{{ log.tierName }}</span>
              <span v-if="log.qrCode" class="history-row__code">· {{ log.qrCode }}</span>
            </div>
            <span class="history-row__time">{{ formatTime(log.createdAt) }}</span>
          </div>
        </div>
      </div>

      <!-- ── TICKET HISTORY MODAL ── -->
      <div v-if="ticketHistoryCode" class="modal-overlay" @click.self="ticketHistoryCode = null">
        <div class="modal-card">
          <button class="modal-close" @click="ticketHistoryCode = null">✕</button>
          <p class="modal-sub">TICKET USAGE HISTORY</p>
          <h3 class="modal-code">{{ ticketHistoryCode }}</h3>

          <div v-if="ticketHistoryLoading" class="state-msg">Loading…</div>
          <template v-else-if="ticketHistory">
            <div class="status-row">
              <span class="status-label">Current Status</span>
              <span class="status-pill" :class="ticketHistory.ticket.status === 'checked_in' ? 'status-pill--in-use' : 'status-pill--ready'">
                {{ ticketHistory.ticket.status === 'checked_in' ? 'In use' : 'Ready to scan' }}
              </span>
            </div>
            <button
              v-if="ticketHistory.ticket.status === 'checked_in'"
              class="manual-exit-btn"
              :disabled="exitLoading"
              @click="doManualExit(ticketHistoryCode)"
            >{{ exitLoading ? '…' : 'Check out for re-entry ↗' }}</button>

            <div class="modal-log-label">LATEST</div>
            <div v-if="!ticketHistory.logs.length" class="state-msg">No entries yet.</div>
            <div v-else class="modal-log">
              <div v-for="log in ticketHistory.logs" :key="log.id" class="log-row">
                <span class="log-icon">{{ logIcon(log.action) }}</span>
                <div class="log-info">
                  <span class="log-action">{{ actionLabel(log.action) }}</span>
                  <span v-if="log.tierName" class="log-tier">{{ log.tierName }} · {{ log.qrCode }}</span>
                </div>
                <span class="log-time">{{ formatDateShort(log.createdAt) }}</span>
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { Html5Qrcode } from 'html5-qrcode'
import ticketingApi from '@/api/ticketing'

export default {
  name: 'CheckinIndex',
  data() {
    return {
      // Login
      tokenInput: '',
      verifierToken: localStorage.getItem('verifierToken') || '',
      loginError: '',
      loginLoading: false,

      // App
      tab: 'scan',
      eventName: localStorage.getItem('checkinEventName') || '',
      exampleCode: '93492UW83MNG',

      // Scan mode: 'in' scans check tickets in, 'out' checks them out
      scanMode: 'in',

      // Scanner
      scanner: null,
      scanResult: null,
      cameraError: '',
      cameras: [],
      selectedCameraId: null,

      // Manual
      manualCode: '',
      manualResult: null,
      manualLoading: false,

      // History
      history: [],
      historyLoading: false,
      historyFilter: 'all',
      historyFilters: [
        { key: 'all', label: 'All' },
        { key: 'entry', label: 'Entry' },
        { key: 'exit', label: 'Exit' },
      ],

      // Ticket history modal
      ticketHistoryCode: null,
      ticketHistory: null,
      ticketHistoryLoading: false,
      exitLoading: false,
    }
  },
  computed: {
    filteredHistory() {
      if (this.historyFilter === 'all') return this.history
      return this.history.filter((l) => l.action === this.historyFilter)
    },
  },
  mounted() {
    // Organisers hand staff a verifier link (/checkin?token=…): sign straight in,
    // then drop the token from the address bar so it isn't left in history.
    const linkToken = this.$route.query.token
    if (linkToken) {
      const query = { ...this.$route.query }
      delete query.token
      this.$router.replace({ query })
      if (linkToken !== this.verifierToken) {
        this.signOut()
        this.tokenInput = String(linkToken)
        this.doLogin()
        return
      }
    }
    if (this.verifierToken) {
      this.startScanner()
      // A link the phone already holds may have been deactivated since: check it
      // now rather than leaving the scanner open until the next scan fails.
      this.loadSession()
    }
  },
  beforeDestroy() {
    this.stopScanner()
  },
  methods: {
    async doLogin() {
      this.loginError = ''
      this.loginLoading = true
      try {
        await ticketingApi.getCheckinHistory(this.tokenInput.trim())
        this.verifierToken = this.tokenInput.trim()
        localStorage.setItem('verifierToken', this.verifierToken)
        this.tokenInput = ''
        this.$nextTick(() => this.startScanner())
        this.loadSession()
      } catch (err) {
        this.loginError = err && err.response && err.response.status === 401
          ? 'This link is invalid or has been deactivated. Ask the event organiser for a new one.'
          : 'Invalid token. Please check and try again.'
      } finally {
        this.loginLoading = false
      }
    },

    // The header shows which event this link scans for. Best effort: the scanner
    // works without it.
    async loadSession() {
      try {
        const res = await ticketingApi.getVerifierSession(this.verifierToken)
        const session = res.data || res
        if (session && session.eventTitle) {
          this.eventName = session.eventTitle
          localStorage.setItem('checkinEventName', session.eventTitle)
        }
      } catch (err) {
        this.handleVerifierError(err)
      }
    },

    // A 401 means the organiser deactivated this link: drop back to the sign-in
    // screen. Returns true when the error was that.
    handleVerifierError(err) {
      if (!(err && err.response && err.response.status === 401)) return false
      this.signOut()
      this.loginError = 'This link has been deactivated. Ask the event organiser for a new one.'
      return true
    },

    // Reads the reason off a failed scan: a 403 is the wrong gate for this link
    // (the API says which), anything else is a ticket we don't recognise.
    scanFailure(err) {
      if (this.handleVerifierError(err)) return null
      const status = err && err.response && err.response.status
      const message = err && err.response && err.response.data && err.response.data.msg
      if (status === 403 && message) return { result: 'invalid', message }
      return { result: 'invalid' }
    },

    signOut() {
      this.stopScanner()
      this.verifierToken = ''
      localStorage.removeItem('verifierToken')
      localStorage.removeItem('checkinEventName')
      this.eventName = ''
      this.history = []
      this.scanResult = null
    },

    switchTab(t) {
      if (t !== 'scan') this.stopScanner()
      this.tab = t
      if (t === 'scan') this.$nextTick(() => this.startScanner())
    },

    setScanMode(mode) {
      this.scanMode = mode
      this.manualResult = null
    },

    async startScanner() {
      this.cameraError = ''

      // Enumerate cameras once, then pick the best default
      if (!this.cameras.length) {
        try {
          const devices = await Html5Qrcode.getCameras()
          this.cameras = devices
          if (!this.selectedCameraId) {
            // Prefer a back camera that isn't telephoto/zoom
            const wide = devices.find(d =>
              /back|rear|environment/i.test(d.label) && !/tele|zoom|periscope/i.test(d.label)
            ) || devices.find(d => /back|rear|environment/i.test(d.label)) || devices[0]
            this.selectedCameraId = wide?.id || null
          }
        } catch {
          // Fall back to facingMode constraint if enumeration fails
        }
      }

      this.$nextTick(() => {
        if (this.scanner) return
        this.scanner = new Html5Qrcode('qr-reader')
        const cameraConstraint = this.selectedCameraId
          ? this.selectedCameraId
          : { facingMode: 'environment' }
        this.scanner
          .start(
            cameraConstraint,
            { fps: 10, qrbox: { width: 260, height: 260 } },
            (decoded) => this.onScanSuccess(decoded),
            () => {},
          )
          .catch((err) => {
            this.cameraError = 'Camera unavailable: ' + (err?.message || err)
            this.scanner = null
          })
      })
    },

    stopScanner() {
      if (this.scanner) {
        this.scanner.stop().then(() => {
          this.scanner.clear()
          this.scanner = null
        }).catch(() => { this.scanner = null })
      }
    },

    cameraLabel(cam) {
      const label = cam.label || ''
      if (/tele|zoom|periscope/i.test(label)) return '🔭 Tele'
      if (/ultra|wide/i.test(label)) return '🔲 Ultra-wide'
      if (/back|rear|environment/i.test(label)) return '📷 Wide'
      if (/front|user|selfie/i.test(label)) return '🤳 Front'
      // Fallback: shorten the raw label
      return label.replace(/\s*\(.*?\)\s*/g, '').trim() || `Camera ${this.cameras.indexOf(cam) + 1}`
    },

    async switchCamera(id) {
      this.selectedCameraId = id
      await new Promise(resolve => {
        if (this.scanner) {
          this.scanner.stop().then(() => { this.scanner.clear(); this.scanner = null; resolve() }).catch(() => { this.scanner = null; resolve() })
        } else resolve()
      })
      this.startScanner()
    },

    async onScanSuccess(qrCode) {
      this.stopScanner()
      try {
        const res = this.scanMode === 'out'
          ? await ticketingApi.checkout(qrCode, this.verifierToken)
          : await ticketingApi.checkin(qrCode, this.verifierToken)
        this.scanResult = res.data || res
      } catch (err) {
        this.scanResult = this.scanFailure(err)
      }
      if (!this.verifierToken) return // signed out by a deactivated link
      // Reload history silently
      this.loadHistory(true)
    },

    dismissResult() {
      this.scanResult = null
      this.startScanner()
    },

    async doManualVerify() {
      const code = this.manualCode.trim()
      if (!code) return
      this.manualLoading = true
      this.manualResult = null
      try {
        const res = this.scanMode === 'out'
          ? await ticketingApi.checkout(code, this.verifierToken)
          : await ticketingApi.checkin(code, this.verifierToken)
        this.manualResult = res.data || res
      } catch (err) {
        this.manualResult = this.scanFailure(err)
      } finally {
        this.manualLoading = false
      }
    },

    manualResultLabel(result) {
      return {
        valid: 'Valid',
        reentry: 'Re-entry',
        used: 'Used',
        reentry_blocked: 'Checked out, re-entry not allowed',
        checked_out: 'Checked Out',
        not_checked_in: 'Not Checked In',
        invalid: 'Invalid',
      }[result] || result
    },

    async loadHistory(silent = false) {
      if (!silent) this.historyLoading = true
      try {
        const res = await ticketingApi.getCheckinHistory(this.verifierToken)
        this.history = res.data || res
      } catch (err) {
        this.handleVerifierError(err)
      } finally {
        this.historyLoading = false
      }
    },

    async openTicketHistory(qrCode) {
      if (!qrCode) return
      this.ticketHistoryCode = qrCode
      this.ticketHistory = null
      this.ticketHistoryLoading = true
      try {
        const res = await ticketingApi.getTicketHistory(qrCode, this.verifierToken)
        this.ticketHistory = res.data || res
      } catch {
        this.ticketHistory = null
      } finally {
        this.ticketHistoryLoading = false
      }
    },

    async doManualExit(qrCode) {
      this.exitLoading = true
      try {
        await ticketingApi.manualExit(qrCode, this.verifierToken)
        await this.openTicketHistory(qrCode)
      } catch {
        alert('Failed to record manual exit.')
      } finally {
        this.exitLoading = false
      }
    },

    actionLabel(action) {
      return { entry: 'Entry', exit: 'Exit', manual_exit: 'Manual Exit', manual_entry: 'Manual Entry' }[action] || action
    },

    logIcon(action) {
      return { entry: '↙', exit: '↗', manual_exit: '↗', manual_entry: '↙' }[action] || '·'
    },

    formatTime(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    },

    formatDateShort(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')} · ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`
    },
  },
}
</script>

<style scoped lang="scss">
$bg: #f8f8fa;
$surface: #ffffff;
$text: #0a0a0a;
$muted: rgba(10,10,10,0.45);
$border: rgba(0,0,0,0.1);
$radius-lg: 28px;
$radius-pill: 44px;
$font: 'Inter', sans-serif;

* { box-sizing: border-box; margin: 0; padding: 0; }

.checkin-app {
  font-family: $font;
  background: $bg;
  min-height: 100vh;
  color: $text;
}

/* ── LOGIN ── */
.login-screen {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.login-card {
  background: $surface;
  border-radius: $radius-lg;
  padding: 40px 36px;
  width: 100%;
  max-width: 380px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.login-logo {
  font-size: 22px;
  font-weight: 900;
  letter-spacing: -0.02em;
}

.login-title {
  font-size: 24px;
  font-weight: 800;
}

.login-sub {
  font-size: 14px;
  color: $muted;
}

.login-input {
  border: 1px solid $border;
  border-radius: 16px;
  padding: 14px 16px;
  font-size: 15px;
  font-family: $font;
  outline: none;
  width: 100%;

  &:focus { border-color: rgba(0,0,0,0.35); }
}

.login-error {
  font-size: 13px;
  color: #c0392b;
}

.login-btn {
  background: $text;
  color: #fff;
  border: none;
  border-radius: $radius-pill;
  padding: 14px 28px;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;

  &:disabled { opacity: 0.4; cursor: not-allowed; }
}

.login-hint {
  font-size: 12px;
  color: $muted;
  text-align: center;
}

/* ── HEADER ── */
.app-header {
  background: $surface;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid $border;
}

.app-event-label {
  font-size: 12px;
  color: $muted;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 2px;
}

.app-entry-badge {
  font-size: 13px;
  font-weight: 800;
  background: $text;
  color: #fff;
  padding: 3px 12px;
  border-radius: $radius-pill;

  &--out { background: #e67e22; }
}

.signout-btn {
  background: none;
  border: 1px solid $border;
  border-radius: $radius-pill;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  color: $muted;
}

/* ── TABS ── */
.app-tabs {
  display: flex;
  background: $surface;
  border-bottom: 2px solid $border;
}

.app-tab {
  flex: 1;
  padding: 12px 8px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  color: $muted;

  &.active {
    color: $text;
    border-bottom-color: $text;
  }
}

/* ── TAB PANELS ── */
.tab-panel {
  flex: 1;

  &--padded {
    padding: 24px 20px;
    max-width: 640px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}

/* ── SCAN MODE TOGGLE ── */
.scan-mode-toggle {
  display: flex;
  gap: 8px;
  padding: 14px 20px;
  background: $surface;
  border-bottom: 1px solid $border;

  &--hidden { display: none; }
}

.scan-mode-btn {
  flex: 1;
  padding: 10px 8px;
  border-radius: $radius-pill;
  border: 1.5px solid $border;
  background: transparent;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  color: $muted;
  transition: all 0.15s;

  &.active {
    background: #27ae60;
    border-color: #27ae60;
    color: #fff;
  }

  &--out.active {
    background: #e67e22;
    border-color: #e67e22;
  }
}

/* ── CAMERA ── */
.camera-wrap {
  background: #000;
  min-height: calc(100vh - 174px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  #qr-reader {
    width: 100%;
    max-width: 480px;
  }
}

.camera-error {
  color: #f8d7da;
  font-size: 14px;
  padding: 16px;
  text-align: center;
}

/* ── RESULT OVERLAY ── */
.result-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  cursor: pointer;
  z-index: 100;

  &--valid          { background: #27ae60; }
  &--reentry        { background: #2980b9; }
  &--invalid        { background: #e74c3c; }
  &--used           { background: #e67e22; }
  &--reentry_blocked { background: #6c3483; }
  &--checked_out    { background: #8e44ad; }
  &--not_checked_in { background: #c0392b; }
}

.result-icon {
  font-size: 72px;
  color: rgba(255,255,255,0.9);
  line-height: 1;
}

.result-word {
  font-size: 52px;
  font-weight: 900;
  color: #fff;
  letter-spacing: -0.01em;
}

.result-tier {
  font-size: 18px;
  font-weight: 700;
  color: rgba(255,255,255,0.85);
}

.result-event {
  font-size: 15px;
  color: rgba(255,255,255,0.7);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.result-hint {
  font-size: 14px;
  color: rgba(255,255,255,0.55);
  margin-top: 24px;
}

.result-entry-count {
  font-size: 20px;
  font-weight: 700;
  color: rgba(255,255,255,0.9);
  background: rgba(0,0,0,0.2);
  padding: 4px 16px;
  border-radius: 20px;
}

/* ── Camera selector ── */
.camera-selector {
  display: flex;
  gap: 8px;
  justify-content: center;
  padding: 10px 16px 0;
  flex-wrap: wrap;
}

.camera-btn {
  padding: 6px 14px;
  border-radius: 20px;
  border: 1.5px solid #ddd;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  color: #555;
  cursor: pointer;
  transition: all 0.15s;

  &--active {
    background: #111;
    color: #fff;
    border-color: #111;
  }
}

/* ── MANUAL TAB ── */
.panel-event-label {
  font-size: 11px;
  font-weight: 700;
  color: $muted;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.panel-title {
  font-size: 24px;
  font-weight: 800;
}

.field-label {
  font-size: 13px;
  font-weight: 700;
  color: $muted;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  .required { color: #c0392b; }
}

.field-input {
  border: 1px solid $border;
  border-radius: 16px;
  padding: 13px 16px;
  font-size: 15px;
  font-family: $font;
  outline: none;
  width: 100%;

  &--sm { width: 110px; flex-shrink: 0; }
  &:focus { border-color: rgba(0,0,0,0.35); }
}

.manual-result {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: rgba(0,0,0,0.03);
  border-radius: 16px;
  padding: 14px 16px;
  flex-wrap: wrap;

  &__info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__tier { font-size: 15px; font-weight: 700; color: $text; }
  &__code { font-size: 12px; font-family: monospace; color: $muted; }
  &__invalid-text { font-size: 14px; color: #c0392b; font-weight: 600; }
}

.manual-badge {
  font-size: 13px;
  font-weight: 800;
  padding: 4px 14px;
  border-radius: $radius-pill;

  &--valid          { background: #d4edda; color: #155724; }
  &--reentry        { background: #d1ecf1; color: #0c5460; }
  &--used           { background: #fff3cd; color: #856404; }
  &--reentry_blocked { background: #e6d6f0; color: #4a235a; }
  &--invalid        { background: #f8d7da; color: #721c24; }
  &--checked_out    { background: #e6d6f0; color: #4a235a; }
  &--not_checked_in { background: #f8d7da; color: #721c24; }
}

.verify-btn {
  background: linear-gradient(135deg, #c4b5fd 0%, #f9a8d4 100%);
  color: $text;
  border: none;
  border-radius: $radius-pill;
  padding: 14px 32px;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;

  &:disabled { opacity: 0.4; cursor: not-allowed; }
}

.history-link-btn {
  background: none;
  border: none;
  color: $text;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
}

/* ── HISTORY TAB ── */
.history-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-chip {
  padding: 6px 16px;
  border-radius: $radius-pill;
  border: 1px solid $border;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  color: $muted;

  &.active {
    background: $text;
    color: #fff;
    border-color: $text;
  }
}

.history-list {
  display: flex;
  flex-direction: column;
}

.history-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  cursor: pointer;

  &:hover { opacity: 0.75; }

  &__info {
    flex: 1;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;
  }

  &__action { font-size: 14px; font-weight: 700; }
  &__tier   { font-size: 13px; color: $muted; }
  &__code   { font-size: 12px; color: $muted; font-family: monospace; }
  &__time   { font-size: 12px; color: $muted; flex-shrink: 0; }
}

.history-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;

  &--entry        { background: #27ae60; }
  &--exit         { background: #3498db; }
  &--manual_exit  { background: #f39c12; }
  &--manual_entry { background: #9b59b6; }
}

/* ── TICKET HISTORY MODAL ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 200;

  @media (min-width: 640px) { align-items: center; }
}

.modal-card {
  background: $surface;
  border-radius: 28px 28px 0 0;
  padding: 32px 24px 48px;
  width: 100%;
  max-width: 520px;
  max-height: 85vh;
  overflow-y: auto;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;

  @media (min-width: 640px) {
    border-radius: 28px;
    max-height: 80vh;
  }
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 24px;
  background: rgba(0,0,0,0.06);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-sub {
  font-size: 11px;
  font-weight: 700;
  color: $muted;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.modal-code {
  font-size: 20px;
  font-weight: 800;
  font-family: monospace;
  letter-spacing: 0.06em;
}

.status-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-label {
  font-size: 14px;
  font-weight: 700;
  color: $muted;
}

.status-pill {
  font-size: 13px;
  font-weight: 700;
  padding: 4px 14px;
  border-radius: $radius-pill;

  &--in-use { background: #f39c12; color: #fff; }
  &--ready  { background: #d4edda; color: #155724; }
}

.manual-exit-btn {
  background: none;
  border: none;
  color: #e74c3c;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
  text-align: left;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.modal-log-label {
  font-size: 11px;
  font-weight: 700;
  color: $muted;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-top: 4px;
}

.modal-log {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.log-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0,0,0,0.06);
}

.log-icon {
  font-size: 18px;
  color: $muted;
  width: 20px;
  text-align: center;
}

.log-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.log-action { font-size: 14px; font-weight: 700; }
.log-tier   { font-size: 12px; color: $muted; }
.log-time   { font-size: 12px; color: $muted; white-space: nowrap; }

/* ── SHARED ── */
.state-msg {
  font-size: 14px;
  color: $muted;
  padding: 12px 0;
  &--error { color: #c0392b; }
}
</style>
